import { computed } from "mobx";
import { NetworkTrafficRecordingDatabaseState } from '../NetworkTrafficRecordingDatabaseState';
import { RecordingUpdated } from "../StateEvents";
import { DbRecording, UUID } from "./DatabaseTypes";
import { DbRecordingEntryReader } from "./DbRecordingEntryReader";
import { requireEvidenceDatabase } from "./getDatabase";
import type { EvidenceDatabase } from "./EvidenceDbStruct";


export class DbRecordingEntryWriter extends DbRecordingEntryReader {
	/**
	 * Updates the recording recording in the database (for the recordingUuid scoped to this class instance)
	 * and announces the update to other processes.
	 *
	 * @param updatedFields Any fields of the recording record to change.
	 */
	updateRecording = async (fieldsToChange: Partial<Omit<DbRecording, "recordingUuid">>) => {
		const {recordingUuid: recordingUuid} = this.recording;
		// Update within a transaction
		const tx = this.db.transaction("recordings", "readwrite");
		try {
			// Read the old value
			const oldRecording = await tx.store.get(recordingUuid);
			if (oldRecording == null) {
				throw new Error(`Can't update a non-existent recording record for recordingUuid ${recordingUuid}`);
			}
			// Update the modified fields
			const recording = { ...oldRecording, ...fieldsToChange };
			this.setRecording(recording);
			DbRecordingEntryReader.forRecordingUuid(this.recording.recordingUuid)?.setRecording(recording);
			await tx.store.put(recording);
			// End the transaction so that others can write
			tx.commit();
		} catch (e) {
			tx.abort();
			throw e;
		}
		// Announce the change to all the other threads (e.g., the side panel.)
		RecordingUpdated.send({ recordingUuid });
	};

	/**
	 * Stop recording and update its summary information.
	 */
	stopRecording = async () => {
		await this.updateRecording({status: "stopped"});
		const networkTrafficRecordingState = NetworkTrafficRecordingDatabaseState.forRecordingUuid(this.recording.recordingUuid);
		await networkTrafficRecordingState.update();
		const dataSummary = networkTrafficRecordingState.getRecordingDataSummary();
		await this.updateRecording({dataSummary});
	}

	setName = async (name: string | undefined) => {
		await this.updateRecording({name});
	}

	@computed get startTime() { return new Date(this.recording.startTimeIso); }
	@computed get startTimeMs() { return this.startTime.getTime(); }

	static #forRecordingUuid = new Map<UUID, DbRecordingEntryWriter>();
	static override forRecordingUuid = (recordingUuid: UUID) =>
		DbRecordingEntryWriter.#forRecordingUuid.get(recordingUuid); 

	static override fromDbAndRecording = (db: EvidenceDatabase, recording: DbRecording) => {
		let reader = DbRecordingEntryWriter.#forRecordingUuid.get(recording.recordingUuid);
		if (reader == null) {
			reader = new DbRecordingEntryWriter(db, recording);
			DbRecordingEntryWriter.#forRecordingUuid.set(recording.recordingUuid, reader);
		}
		return reader;
	}

	static override fromRecordingUuid = async (recordingUuid: UUID) => {
		let reader = DbRecordingEntryWriter.#forRecordingUuid.get(recordingUuid);
		if (reader == null) {
			const db = await requireEvidenceDatabase();
			const recording = await DbRecordingEntryWriter.fetchFromDb(db, recordingUuid);
			reader = recording != null ? new DbRecordingEntryWriter(db, recording) : undefined;
		}
		return reader;
	}

}

RecordingUpdated.addListener( ({recordingUuid}) => {
	DbRecordingEntryReader.forRecordingUuid(recordingUuid)?.updateFromDatabase();
	DbRecordingEntryWriter.forRecordingUuid(recordingUuid)?.updateFromDatabase();
})